import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { BenefitsService } from '../../../core/service/benefits.service';

import { takeUntil } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { GetFixedBenefitsRequest } from '../../../core/requests/GetFixedBenefitsRequest';
import { GetFixedBenefitsResponse } from '../../../core/responses/GetFixedBenefitsResponse';
import { FixedBenefit } from '../../../core/models/fixed-benefit';
import { AuthenticationService } from '../../../core/service';
import { ActivatedRoute, Router } from '@angular/router';

// This page does not contain filters (yet)
// This means more benefits should be loaded initially
const INITIAL_BENEFITS_TO_LOAD = 8;
const LOAD_MORE_CNT = 4;

@Component({
  selector: 'app-fixed-benefits',
  templateUrl: './fixed-benefits.component.html',
  styleUrls: ['./fixed-benefits.component.scss'],
  providers: [TranslatePipe],
})
export class FixedBenefitsComponent implements OnInit, OnDestroy {
  // Scroll-management variables
  private static savedScrollPosition = 0;

  private ngUnsubscribe = new Subject<void>();
  public fixedBenefits: Array<FixedBenefit> = null;
  public totalBenefits: number;
  public companyId: number = null;
  public subdomain: string;

  constructor(
    private authenticationService: AuthenticationService,
    private benefitsService: BenefitsService,
    private _route: ActivatedRoute,
    private router: Router
  ) {
    this.subdomain = this._route.snapshot.params['subdomain'];
  }

  ngOnInit(): void {
    this.companyId = this.authenticationService.currentUserValue.companyId;
    this.getFixedBenefits()
      .subscribe({
        next: (data) => {
          this.onGetFixedBenefitsSuccess(data);

          this.restoreScroll();
        },
        error: (error) => {
          this.onFixedBenefitsFail(error);
        }
      });
  }

  public ngOnDestroy() {
    FixedBenefitsComponent.savedScrollPosition = window.scrollY;
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Get fixed benefits
   */
  public getFixedBenefits(start: number = 0, limit: number = INITIAL_BENEFITS_TO_LOAD) {
    const params: GetFixedBenefitsRequest = {
      paginationCriteria: {
        start: start,
        limit: limit,
      },
      companyId: this.companyId,
      orderByRank: true
    };

    return this.benefitsService
      .getUserFixedBenefits(params)
      .pipe(takeUntil(this.ngUnsubscribe))
  }

  public getFixedBenefitsWithSubscription(start: number, limit: number = INITIAL_BENEFITS_TO_LOAD) {
    const req = this.getFixedBenefits(start, limit);
    req.subscribe({
      next: (data) => {
        this.onGetFixedBenefitsSuccess(data);
      },
      error: (error) => {
        this.onFixedBenefitsFail(error);
      }
    });
  }

  public onGetFixedBenefitsSuccess(data: GetFixedBenefitsResponse) {
    this.fixedBenefits = [...this.fixedBenefits ?? [], ...data.responseObject.data];
    this.totalBenefits = data.responseObject.totalResults;
  }

  public onFixedBenefitsFail(error: HttpErrorResponse) {}

  /**
   * Helpers
   */
  public loadMoreBenefits() {
    if (this.fixedBenefits && this.fixedBenefits.length === this.totalBenefits)
      return;

    const lim = Math.min(LOAD_MORE_CNT, this.totalBenefits - this.fixedBenefits.length);

    this.getFixedBenefitsWithSubscription(this.fixedBenefits.length, lim);
  }

  public openFixedBenefitInfoPage(fixedBenefitId: number) {
    this.router.navigate([
      '/' + this.subdomain + '/fixed-benefits-info/' + fixedBenefitId,
    ]);
  }

  private restoreScroll() {
    setTimeout(() => {
      window.scrollTo(0, FixedBenefitsComponent.savedScrollPosition);
    }, 0);
  }
}
